import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Personorg } from "@src/app/components/org/orgprojectlist/model/personorg";
import { Project } from "@src/app/models/project";
import { Projectperson } from "@src/app/models/projectperson";
import { ProjectassignService } from "./service/projectassign.service";
import { Projectmember } from "@src/app/components/org/orgprojectlist/model/projectmember";
import { Personproject } from "@src/app/components/org/orgprojectlist/model/personproject";
import { DatastoreService } from "@src/app/helpers/datastore/datastore.service";
import { PersonOrganization } from "@src/app/models/personorganization";
import { Role } from "@src/app/components/org/orgprojectlist/assigntemplateroles/model/role";
import { UieventhubService } from "@src/app/helpers/uieventhub/uieventhub.service";

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalService } from '@src/app/helpers/modal.service';
import { forkJoin } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: "app-projectassign",
  templateUrl: "./projectassign.component.html",
  styleUrls: ["./projectassign.component.css"],
})
export class ProjectassignComponent implements OnInit, OnChanges {
  @Input() project: Project;
  @Input() nomodal: boolean = false;
  @Input() reset: boolean = false;

  projectMembers: Projectperson[] = [];
  assignablePersons: PersonOrganization[] = [];
  orgMembers: PersonOrganization[] = [];
  memberToDelete: Projectperson;
  modalPerson: Projectperson;
  modalorgId: string;
  showSpinner: boolean;
  showDeleteConfirm: boolean;
  errorMessage: string;
  updateMessage: string;
  roles: Role[] = [];
  showroles:boolean;
  @Output() update: string;
  @Input() standalone:boolean;
  @Input() wizard:boolean;

  @ViewChild("rolemodalclose", { static: true }) rolemodalclose: ElementRef;
  @ViewChild("deletemodalclose", { static: true }) deletemodalclose: ElementRef;
  @Output() gotoT = new EventEmitter<boolean>();
  @Output() setProjectload = new EventEmitter<boolean>();
  @Output() closemodal = new EventEmitter<boolean>();
  @Output() doneLoading = new EventEmitter<boolean>();

  constructor(
    private pasrv: ProjectassignService,
    private datastore: DatastoreService,
    private uieventhub: UieventhubService,
    private modalservice:ModalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.project) {
      this.project = changes.project.currentValue;
      this.roles = []; // Clear roles when project changes
      this.assignablePersons = [];
      this.projectMembers = [];
      this.loadLists();
      this.uieventhub.removeLoader('projectassign');
      this.doneLoading.emit(true);
    }
    if (changes.reset) {
      this.reset = changes.reset.currentValue;
      if (this.reset) {
        this.reset = false;
        this.resetState();
        this.closeModal();
      }
    }
  }

  ngOnInit() {
    this.modalPerson = new Projectperson();
    this.memberToDelete = new Projectperson();
        this.roles = []; // Clear roles when project changes
      this.assignablePersons = [];
      this.projectMembers = [];
    // Subscribe to org members and reload lists when they change
    this.datastore.orgMembers$.pipe(untilDestroyed(this)).subscribe((omem) => {
      this.orgMembers = omem;
      if (this.project) {
        this.loadLists();
      }
    });
  }

  setRoles(roles: Role[]) {
    this.roles = roles;
  }

  closeModal() {
    this.closemodal.emit(true);
  }

  resetState() {
    this.assignablePersons = [];
    this.projectMembers = [];
    this.roles = [];
  }

  /**
   * Load both project members and calculate assignable persons
   * This ensures fresh data every time
   */
  loadLists() {
    if (!this.project || !this.orgMembers) return;
    this.datastore.projectpersonList$.pipe(
      tap((projectPeople) => {
        console.log('Loaded project people:', projectPeople);
        this.projectMembers = projectPeople.filter(
          (pp) => pp.projectId === this.project.projectId
        );
        this.calculateAssignablePersons();
        // Reset assignment state on all persons
        this.assignablePersons.forEach(person => {
          person.assigned = false;
          person.projectrole = "";
          person.projectTemplateRole = "none";
        });
       
        this.doneLoading.emit(true);
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  /**
   * Calculate who can be assigned by filtering out already-assigned members
   */
  private calculateAssignablePersons() {
    this.assignablePersons = this.orgMembers.filter(orgMember => 
      !this.projectMembers.some(pm => pm.personId === orgMember.personId)
    );
  }

  removeSelection(personId: string) {
    const person = this.assignablePersons.find(ap => ap.personId === personId);
    if (person) {
      person.assigned = false;
      person.projectrole = "";
      person.projectTemplateRole = "none";
      
      if (this.roles && this.roles.length > 0) {
        this.roles = this.roles.map((r) => {
          if (r.assignedperson === personId) {
            r.assignedperson = "";
          }
          return r;
        });
      }
    }
  }

  assignToProject(goto?: boolean) {
    this.showSpinner = true;
    let error = false;

    // Validate all assigned members have roles
    for (let asp of this.assignablePersons) {
      if (asp.assigned) {
        if (!asp.projectTemplateRole && !asp.projectrole) {
          this.errorMessage = "Please select a role for all assigned members";
          this.showSpinner = false;
          error = true;
          break;
        }
      }
    }

    if (error) return;

    this.setProjectload.emit(true);

    // Prepare assignments
    const assignmentPromises: Promise<any>[] = [];
    const assignedPersons: Personproject[] = [];

    for (let person of this.assignablePersons) {
      if (!person.assigned) continue;

      const assignment: Personproject = {
        projectId: this.project.projectId,
        personId: person.personId,
        fullname: person.membername,
        im: person.im,
        email: person.email,
        phone: person.phone,
        title: this.project.title,
        subtitle: this.project.subtitle || "",
        shortdescription: this.project.shortdescription || "",
        projectdescription: this.project.projectdescription || "",
        projectscope: "organization",
        projectlocation: this.project.projectlocation || "",
        start_date: this.project.start_date,
        end_date: this.project.end_date,
        organization: this.project.organizationId,
        organizationname: this.project.organizationname,
        customer: this.project.customer,
        customername: this.project.customername,
        projectrole: person.projectTemplateRole && person.projectTemplateRole !== 'none'
          ? person.projectTemplateRole
          : person.projectrole,
      };

      assignedPersons.push(assignment);

      assignmentPromises.push(
        new Promise((resolve) => {
          this.pasrv.assignToProject(assignment)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              // Assign template role if applicable
              const role = this.roles?.find((r) => r.projectrole === assignment.projectrole);
              if (role) {
                this.pasrv.assignTemplateRole(this.project, role.roleId, assignment.personId)
                  .pipe(untilDestroyed(this))
                  .subscribe(() => resolve(true), () => resolve(true));
              } else {
                resolve(true);
              }
            }, () => resolve(true));
        })
      );
    }

    // Wait for all assignments to complete, then reload lists
    Promise.all(assignmentPromises).then(() => {
      this.loadLists();
      if (goto) {
        this.gotoT.emit(true);
        this.setProjectload.emit(false);
      }
      this.closeModal();
    });
  }

  assignRole(role: Role, personId: string) {
    if (!role) return;

    this.assignablePersons = this.assignablePersons.map((ap) => {
      if (ap.personId === role.assignedperson) {
        ap.projectrole = role.projectrole;
        ap.projectTemplateRole = role.projectrole;
      } else if (role.assignedperson === null && ap.personId === personId) {
        ap.projectrole = '';
        ap.projectTemplateRole = "none";
      }
      return ap;
    });
  }

  onClick(person: Projectperson) {
    this.modalPerson = person;
  }

  setDeleteMember(member: Projectperson) {
    this.memberToDelete = member;
    this.showDeleteConfirm = true;
  }

  deleteMember() {
    this.pasrv.deleteMember(this.memberToDelete).pipe(untilDestroyed(this)).subscribe(() => {
      this.deletemodalclose.nativeElement.click();
      
      // Remove from project members
      this.projectMembers = this.projectMembers.filter(
        (pm) => pm.personId !== this.memberToDelete.personId
      );
      
      // Add back to assignable persons
      const deletedOrgMember = this.orgMembers.find(
        (om) => om.personId === this.memberToDelete.personId
      );
      if (deletedOrgMember) {
        this.assignablePersons.push(deletedOrgMember);
      }
      
      this.memberToDelete = new Projectperson();
    });
  }

  saveRole() {
    this.pasrv.saveMember(this.modalPerson).pipe(untilDestroyed(this)).subscribe(() => {
      this.modalPerson = new Projectperson();
      this.rolemodalclose.nativeElement.click();
    });
  }
}

